/* global React, window */

// ================================================================
// Step Complementos, entra DEPOIS da papelaria e ANTES da confirmação final.
// Itens que não são livro da curadoria nem papelaria do kit (agenda,
// dicionário, garrafinha, avental, jaleco…), buscados na VTEX com o mesmo
// endpoint da papelaria "Montar do zero" (GET /searchBySeller, ver
// searchComplementosVtex em api.js).
//
// data.complementos = { [seriesIdx]: [{ itemId, qty, name, brand, model,
//   price, imageUrl, vtexProductId, skuId, ean, isBook }] }
// Cada linha guarda o item inteiro (não só o id, como data.papelaria): a
// lista pública lê direto daqui, sem depender de catálogo em memória nem
// de campo novo no progresso salvo do backend. Lista antiga sem o campo =
// nenhum complemento, tudo segue igual.
// ================================================================

// Linhas válidas de uma série, com a posição no array BRUTO (cartIdx) —
// mesmo cuidado de papHere em steps-final.jsx: remover precisa da posição
// real, não da posição na lista já filtrada.
function resolveComplementosForSeries(data, seriesIdx) {
  const raw = ((data && data.complementos) || {})[seriesIdx];
  return (Array.isArray(raw) ? raw : []).map((c, cartIdx) => {
    if (!c || !c.itemId || !c.name) return null;
    const qty = Math.max(1, parseInt(c.qty, 10) || 1);
    const price = typeof c.price === "number" && Number.isFinite(c.price) ? c.price : null;
    return { ...c, qty, price, cartIdx };
  }).filter(Boolean);
}

const cmpMoney = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

// Campos do item de busca que vão salvos no carrinho (sem lixo de UI)
function complementoEntry(item, qty = 1) {
  return {
    itemId: item.itemId,
    qty,
    name: item.name,
    brand: item.brand || "",
    model: item.model || "",
    price: typeof item.price === "number" ? item.price : null,
    imageUrl: item.imageUrl || null,
    vtexProductId: item.vtexProductId || null,
    skuId: item.skuId || null,
    ean: item.ean || null,
    isBook: !!item.isBook,
  };
}

function StepComplementos() {
  const { data, setData, account } = useApp();
  const activeSeries = resolveSeriesList(data);
  const [seriesIdx, setSeriesIdx] = React.useState(0);
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState({ status: "idle", items: [] }); // idle | loading | ready | error
  const safeIdx = seriesIdx < activeSeries.length ? seriesIdx : 0;

  React.useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setSearch({ status: "idle", items: [] }); return undefined; }
    if (!window.ScolistAPI || typeof window.ScolistAPI.searchComplementosVtex !== "function") return undefined;
    let cancelled = false;
    const sellers = (account && account.school && account.school.sellerRestrictions) || [];
    setSearch((s) => ({ status: "loading", items: s.items }));
    const handle = setTimeout(async () => {
      try {
        const items = await window.ScolistAPI.searchComplementosVtex(q, sellers);
        if (!cancelled) setSearch({ status: "ready", items });
      } catch (err) {
        console.error("Falha ao buscar complementos na VTEX:", err);
        if (!cancelled) setSearch({ status: "error", items: [] });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, account]);

  const cart = resolveComplementosForSeries(data, safeIdx);
  const inCart = (itemId) => cart.find((c) => c.itemId === itemId);

  const updateSeries = (idx, fn) => {
    setData((d) => {
      const all = { ...(d.complementos || {}) };
      const cur = Array.isArray(all[idx]) ? all[idx] : [];
      all[idx] = fn(cur);
      return { ...d, complementos: all };
    });
  };

  const add = (item) => updateSeries(safeIdx, (cur) =>
    cur.some((c) => c && c.itemId === item.itemId) ? cur : [...cur, complementoEntry(item)]);

  // Em todas as séries: soma onde ainda não tem, não mexe na quantidade de
  // onde já tem.
  const addToAll = (item) => {
    setData((d) => {
      const all = { ...(d.complementos || {}) };
      activeSeries.forEach((_, i) => {
        const cur = Array.isArray(all[i]) ? all[i] : [];
        if (!cur.some((c) => c && c.itemId === item.itemId)) all[i] = [...cur, complementoEntry(item)];
      });
      return { ...d, complementos: all };
    });
  };

  const remove = (itemId) => updateSeries(safeIdx, (cur) => cur.filter((c) => !c || c.itemId !== itemId));
  const setQty = (itemId, qty) => updateSeries(safeIdx, (cur) =>
    cur.map((c) => (c && c.itemId === itemId ? { ...c, qty: Math.max(1, qty) } : c)));

  const copyCartToAll = () => {
    if (cart.length === 0) return;
    setData((d) => {
      const all = { ...(d.complementos || {}) };
      activeSeries.forEach((_, i) => {
        if (i === safeIdx) return;
        const cur = Array.isArray(all[i]) ? all[i] : [];
        const missing = cart.filter((c) => !cur.some((x) => x && x.itemId === c.itemId))
          .map((c) => complementoEntry(c, c.qty));
        if (missing.length) all[i] = [...cur, ...missing];
      });
      return { ...d, complementos: all };
    });
  };

  const total = cart.reduce((s, c) => s + (c.price || 0) * c.qty, 0);
  const results = search.items;

  return (
    <div>
      <Heading
        eyebrow="Complementos · Itens complementares"
        title="Algo mais pra lista?"
        subtitle="Agenda, dicionário, garrafinha, avental… Busque na loja e adicione o que a série precisa além dos livros e da papelaria. Os complementos aparecem na lista das famílias e vão junto pro carrinho." />

      {/* Series tabs */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 18 }}>
        {activeSeries.map((s, i) => {
          const count = resolveComplementosForSeries(data, i).length;
          const active = i === safeIdx;
          return (
            <button key={s + i} onClick={() => setSeriesIdx(i)} style={{
              padding: "8px 14px", borderRadius: 10,
              background: active ? "var(--teal)" : "transparent",
              color: active ? "#fff" : "var(--text)",
              border: `1px solid ${active ? "var(--teal)" : "var(--border)"}`,
              fontSize: 13, fontWeight: active ? 700 : 500,
              display: "inline-flex", alignItems: "center", gap: 7,
            }}>
              {s}
              {count > 0 && <span style={{
                padding: "1px 7px", fontSize: 10.5, fontWeight: 700, borderRadius: 9,
                background: active ? "rgba(255,255,255,0.3)" : "var(--green)",
                color: active ? "#fff" : "var(--text)",
              }}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 380px", gap: 24, alignItems: "start" }}>
        {/* ========== LEFT: SEARCH + RESULTS ========== */}
        <div>
          <div style={{
            display: "flex", gap: 10, alignItems: "center", marginBottom: 14,
            background: "#fff", padding: "10px 14px", borderRadius: 12,
            border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)",
          }}>
            <span style={{ fontSize: 18 }}>🔍</span>
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar: agenda, dicionário, garrafinha…"
              style={{ flex: 1, border: "none", outline: "none", fontSize: 15, background: "transparent", color: "var(--text)" }} />
            {search.status === "loading" && <span style={{ fontSize: 12, color: "var(--text-mute)" }}>buscando…</span>}
            {query && <button onClick={() => setQuery("")} style={{ fontSize: 12, color: "var(--text-mute)" }}>limpar</button>}
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            {search.status === "idle" && (
              <div style={{ padding: "60px 40px", textAlign: "center", background: "#fff", borderRadius: 14, border: "1px dashed var(--border)" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🧩</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>
                  Busque um complemento na loja
                </div>
                <div style={{ fontSize: 13, color: "var(--text-mute)", maxWidth: 380, margin: "0 auto", lineHeight: 1.5 }}>
                  Digite algo como <strong>"agenda"</strong>, <strong>"dicionário"</strong> ou <strong>"garrafa"</strong>.
                  <br/>Esta etapa é opcional, se não precisar, é só avançar.
                </div>
              </div>
            )}
            {search.status === "error" && (
              <div style={{ padding: 40, textAlign: "center", color: "#c53030", fontSize: 14 }}>
                Não consegui buscar na loja agora. Tente de novo em instantes.
              </div>
            )}
            {search.status === "ready" && results.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text-mute)", fontSize: 14 }}>
                Nenhum item encontrado pra "<strong>{query}</strong>".
                <div style={{ fontSize: 12, marginTop: 8 }}>Tente um termo mais genérico.</div>
              </div>
            )}
            {results.map((item) => (
              <ComplementoResultCard key={item.itemId} item={item}
                added={!!inCart(item.itemId)}
                multiSeries={activeSeries.length > 1}
                onAdd={() => add(item)}
                onAddAll={() => addToAll(item)}
                onRemove={() => remove(item.itemId)} />
            ))}
          </div>
        </div>

        {/* ========== RIGHT: CART ========== */}
        <div style={{
          position: "sticky", top: 96,
          background: "#fff", borderRadius: 16, border: "1px solid var(--border)",
          boxShadow: "var(--shadow-sm)", padding: 18,
        }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--teal-dark)", textTransform: "uppercase", letterSpacing: 1 }}>
              {activeSeries[safeIdx]}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-mute)" }}>
              {cart.length} item{cart.length !== 1 ? "s" : ""}
            </div>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 14 }}>Complementos desta série</div>

          {cart.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "var(--text-mute)", fontSize: 12.5, border: "1px dashed var(--border)", borderRadius: 10 }}>
              Nenhum complemento ainda.
              <br />Busque e adicione itens ao lado.
            </div>
          )}

          <div style={{ display: "grid", gap: 8, maxHeight: 480, overflowY: "auto" }}>
            {cart.map((c) => (
              <div key={c.itemId} style={{ padding: "10px 12px", background: "var(--bg-light)", borderRadius: 10 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <ComplementoThumb item={c} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", lineHeight: 1.3, marginBottom: 3 }}>{c.name}</div>
                    <div style={{ fontSize: 10.5, color: "var(--text-mute)" }}>
                      {[c.brand, c.price != null ? cmpMoney(c.price) : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <button onClick={() => remove(c.itemId)} style={{ fontSize: 14, color: "var(--text-mute)", padding: 2 }} title="Remover">✕</button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <button onClick={() => (c.qty <= 1 ? remove(c.itemId) : setQty(c.itemId, c.qty - 1))} style={cmpQtyBtnStyle}>−</button>
                  <span style={{ minWidth: 22, textAlign: "center", fontSize: 13, fontWeight: 700 }}>{c.qty}</span>
                  <button onClick={() => setQty(c.itemId, c.qty + 1)} style={cmpQtyBtnStyle}>+</button>
                  <span style={{ fontSize: 10.5, color: "var(--text-mute)" }}>un</span>
                </div>
              </div>
            ))}
          </div>

          {cart.length > 0 && (
            <>
              <div style={{ height: 1, background: "var(--border)", margin: "14px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, color: "var(--teal-dark)" }}>
                <span>Estimativa</span><span>{cmpMoney(total)}</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--text-mute)", marginTop: 3 }}>
                preços de referência da loja, podem mudar até a compra
              </div>
              {activeSeries.length > 1 && (
                <button onClick={copyCartToAll} style={{
                  marginTop: 14, width: "100%", padding: "9px 12px", borderRadius: 10,
                  background: "#fff", border: "1px solid var(--teal)", color: "var(--teal-dark)",
                  fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                }}>
                  Copiar estes complementos pra todas as séries
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ComplementoThumb({ item, size = 56 }) {
  const [failed, setFailed] = React.useState(false);
  if (!item.imageUrl || failed) {
    return (
      <div style={{
        width: size, height: size, borderRadius: 8, flexShrink: 0,
        background: "var(--bg-light)", border: "1px solid var(--border)",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.42,
      }}>{item.isBook ? "📖" : "🧩"}</div>
    );
  }
  return <img src={item.imageUrl} alt="" loading="lazy" onError={() => setFailed(true)}
    style={{ width: size, height: size, objectFit: "contain", borderRadius: 8, background: "#fff", border: "1px solid var(--border)", flexShrink: 0 }} />;
}

function ComplementoResultCard({ item, added, multiSeries, onAdd, onAddAll, onRemove }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 12, padding: 14,
      border: `1px solid ${added ? "var(--teal)" : "var(--border)"}`,
      boxShadow: added ? "0 0 0 1px var(--teal)" : "var(--shadow-sm)",
      display: "flex", gap: 14, alignItems: "center",
    }}>
      <ComplementoThumb item={item} size={64} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text)", lineHeight: 1.3 }}>{item.name}</div>
        <div style={{ fontSize: 11.5, color: "var(--text-mute)", marginTop: 3 }}>
          {[item.brand, item.model && item.model !== item.name ? item.model : null, item.isBook ? "Livro" : null].filter(Boolean).join(" · ")}
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "var(--teal-dark)", marginTop: 6 }}>
          {item.price ? cmpMoney(item.price) : <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-mute)" }}>sem preço na loja</span>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
        {added ? (
          <button onClick={onRemove} style={{
            padding: "7px 12px", borderRadius: 10, background: "var(--teal)", color: "#fff",
            border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }} title="Clique pra tirar desta série">✓ Na lista</button>
        ) : (
          <button onClick={onAdd} style={{
            padding: "7px 12px", borderRadius: 10, background: "#fff", color: "var(--teal-dark)",
            border: "1px solid var(--teal)", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>+ Adicionar</button>
        )}
        {multiSeries && (
          <button onClick={onAddAll} style={{ fontSize: 11, color: "var(--text-mute)", textDecoration: "underline", textUnderlineOffset: 2 }}>
            em todas as séries
          </button>
        )}
      </div>
    </div>
  );
}

const cmpQtyBtnStyle = {
  width: 22, height: 22, borderRadius: 6,
  background: "#fff", border: "1px solid var(--border)",
  fontSize: 14, fontWeight: 700, color: "var(--teal-dark)",
  display: "flex", alignItems: "center", justifyContent: "center",
};

Object.assign(window, {
  StepComplementos,
  resolveComplementosForSeries,
});
